export type IFileUploadItemState = {
    id: string
    name: string
    retryKey?: string
    status: 'queued' | 'uploading' | 'confirming' | 'uploaded' | 'failed'
    error?: string
}

export type IFileUploadState =
    | { state: 'idle'; id: number }
    | {
          state: 'uploading' | 'done'
          numberOfFiles: number
          uploaded: number
          failed: number
          items: IFileUploadItemState[]
          id: number
      }

/** Serializes selections while keeping every queued selection busy and visible. */
export function createDirectoryFileUploadQueue(onStateChange: (state: IFileUploadState) => void) {
    let state: IFileUploadState = { state: 'idle', id: 0 }
    let pending = 0
    let batchId = 0
    let tail = Promise.resolve()

    const publish = (items: IFileUploadItemState[]) => {
        state = {
            state: pending > 0 ? 'uploading' : 'done',
            id: state.id,
            numberOfFiles: items.length,
            uploaded: items.filter((item) => item.status === 'uploaded').length,
            failed: items.filter((item) => item.status === 'failed').length,
            items,
        }
        onStateChange(state)
    }

    const updateItem = (id: string, update: Partial<IFileUploadItemState>) => {
        if (state.state === 'idle') return
        publish(state.items.map((item) => (item.id === id ? { ...item, ...update } : item)))
    }

    return {
        enqueue<T>(
            incoming: Omit<IFileUploadItemState, 'id'>[],
            run: (items: IFileUploadItemState[], update: typeof updateItem) => Promise<T>
        ) {
            const items = incoming.map((item, index) => ({ ...item, id: `${++batchId}:${index}` }))
            const retryKeys = new Set(incoming.map((item) => item.retryKey).filter(Boolean))
            const previousItems =
                state.state === 'idle' || (pending === 0 && state.failed === 0)
                    ? []
                    : state.items.filter((item) => item.status !== 'failed' || !retryKeys.has(item.retryKey))
            if (previousItems.length === 0) state = { state: 'idle', id: batchId }
            pending += 1
            publish([...previousItems, ...items])

            const result = tail.then(async () => {
                try {
                    return await run(items, updateItem)
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Upload failed'
                    if (state.state !== 'idle') {
                        const unfinished = new Set(items.map((item) => item.id))
                        publish(
                            state.items.map((item) =>
                                unfinished.has(item.id) && item.status !== 'uploaded' && item.status !== 'failed'
                                    ? { ...item, status: 'failed', error: message }
                                    : item
                            )
                        )
                    }
                    throw error
                } finally {
                    pending -= 1
                    if (state.state !== 'idle') publish(state.items)
                }
            })
            // A failed operation must not block later queued selections.
            tail = result.then(
                () => undefined,
                () => undefined
            )
            return result
        },
        resetCompleted() {
            if (pending > 0 || state.state === 'idle' || state.failed > 0) return
            state = { state: 'idle', id: state.id }
            onStateChange(state)
        },
    }
}
